import React from 'react';
import { Banner, SlimBanner, Button } from '@stitch-fix/mode-react';
import { useViewEvent, useSelectEvent } from '@stitch-fix/event-reporter';
import { useLocalStorageValue } from '@react-hookz/web';
import type {
  ModeBannerSitewideVariant,
  ModeBannerSlimVariant,
} from '../../../types/definitions/graphql-schema';
import type {
  BannersProps,
  GraphqlBanner,
  LegacySiteWideBanner,
  LegacySlimBanner,
  SiteWideThemesType,
  SlimThemesType,
} from './BannerTypes';
import BannerIcon, { isValidIcon } from './BannerIcon';

export const SlimVariants: Record<ModeBannerSlimVariant, SlimThemesType> = {
  DEFAULT: 'info-light',
  INVERSE: 'dark',
  PROMOTIONAL: 'promo-dark',
  URGENT: 'error-dark',
  CITRUS: 'citrus',
  GRAPEFRUIT: 'grapefruit',
  FOREST: 'forest',
  NAVY_WHITE: 'navy-white',
  NAVY_CITRUS: 'navy-citrus',
} as const;

const SiteWideVariants: Record<ModeBannerSitewideVariant, SiteWideThemesType> =
  {
    PROMOTIONAL: 'promo-dark',
    ERROR: 'error-dark',
    INFORMATIONAL: 'info-dark',
    CITRUS: 'citrus',
    GRAPEFRUIT: 'grapefruit',
  } as const;

const BannerButton = ({
  href,
  text,
  bannerVariant,
  isSecondary = false,
  onClick,
}: {
  href: string;
  text: string;
  bannerVariant: SiteWideThemesType;
  isSecondary?: boolean;
  onClick?: () => void;
}) => {
  const variant = (() => {
    const useDarkText =
      bannerVariant === 'promo-dark' ||
      bannerVariant === 'citrus' ||
      bannerVariant === 'grapefruit';

    if (isSecondary) {
      return useDarkText ? 'text' : 'text-inverse';
    }

    return useDarkText ? 'outline' : 'outline-inverse';
  })();

  return (
    <Button as="a" href={href} variant={variant} width="fit" onClick={onClick}>
      {text}
    </Button>
  );
};

const LegacySiteWide = ({
  source,
  onClose,
}: {
  source: LegacySiteWideBanner;
  onClose: () => void;
}) => {
  const { reportSelectEvent } = useSelectEvent();

  return (
    <Banner
      headline={source.headline}
      variant={source.variant}
      primaryLink={
        source.cta ? (
          <BannerButton
            href={source.cta.href}
            text={source.cta.text}
            bannerVariant={source.variant}
            onClick={() => {
              reportSelectEvent(getBannerClickData({ bannerId: source.id }));
            }}
          />
        ) : undefined
      }
      onClose={onClose}
    >
      {source.message}
    </Banner>
  );
};

const LegacySlim = ({
  source,
  onClose,
}: {
  source: LegacySlimBanner;
  onClose: () => void;
}) => {
  const { reportSelectEvent } = useSelectEvent();

  return (
    <SlimBanner
      cta={
        source.cta
          ? (() => {
              if ('onClick' in source.cta) {
                const ctaWithOnClick = source.cta;
                return {
                  ...ctaWithOnClick,
                  onClick: () => {
                    reportSelectEvent(
                      getBannerClickData({ bannerId: source.id }),
                    );
                    ctaWithOnClick.onClick?.();
                  },
                };
              }
              const ctaWithUrl = source.cta;
              return {
                ...ctaWithUrl,
                onClick: () => {
                  reportSelectEvent(
                    getBannerClickData({ bannerId: source.id }),
                  );
                },
              };
            })()
          : undefined
      }
      icon={source.icon}
      text={source.text}
      variant={source.variant}
      onClose={onClose}
    />
  );
};

const getBannerClickData = ({
  bannerId,
  isSecondaryCta = false,
}: {
  bannerId: string;
  isSecondaryCta?: boolean;
}) => {
  return {
    schema: 'select' as const,
    content_id: bannerId,
    name: isSecondaryCta ? 'banner_secondary_action' : 'banner_primary_action',
    action_name: isSecondaryCta
      ? 'open_banner_secondary_action'
      : 'open_banner_primary_action',
  } as const;
};

const GraphqlBanner = ({
  source,
  onClose,
}: {
  source: GraphqlBanner;
  onClose: () => void;
}) => {
  const { reportSelectEvent } = useSelectEvent();

  const icon =
    source.icon && isValidIcon(source.icon) ? (
      <BannerIcon icon={source.icon} />
    ) : null;

  // eslint-disable-next-line no-underscore-dangle
  if (source.__typename === 'ModeBannerSitewide') {
    const variant = SiteWideVariants[source.sitewideVariant];

    return (
      <Banner
        icon={icon}
        headline={source.headline}
        variant={variant}
        primaryLink={
          source.cta ? (
            <BannerButton
              href={source.cta.url}
              text="primary"
              bannerVariant={variant}
              onClick={() => {
                reportSelectEvent(getBannerClickData({ bannerId: source.id }));
              }}
            />
          ) : undefined
        }
        secondaryLink={
          source.secondaryCta ? (
            <BannerButton
              href={source.secondaryCta.url}
              text={source.secondaryCta.label.text}
              bannerVariant={variant}
              isSecondary
              onClick={() => {
                reportSelectEvent(
                  getBannerClickData({
                    bannerId: source.id,
                    isSecondaryCta: true,
                  }),
                );
              }}
            />
          ) : undefined
        }
        onClose={onClose}
      >
        {source.message}
      </Banner>
    );
  }

  // slim
  return (
    <SlimBanner
      variant={SlimVariants[source.slimVariant]}
      text={source.message}
      icon={icon}
      cta={
        source.cta
          ? {
              text: source.cta.label.text,
              url: source.cta.url,
              onClick: () => {
                reportSelectEvent(getBannerClickData({ bannerId: source.id }));
              },
            }
          : undefined
      }
      onClose={onClose}
    />
  );
};

interface ClosedBannersCache {
  [key: string]: {
    closed: boolean;
  };
}

/**
 * Controls all banners in Knit, both legacy prop based and GraphQL
 */
export const Banners = ({
  legacySiteWideBanner,
  legacySlimBanner,
  graphQLBanners,
}: BannersProps) => {
  const [storedIDs, setStoredIDs] = useLocalStorageValue<ClosedBannersCache>(
    `Knit:Banners`,
    {},
  );

  const isBannerDismissed = (id: string) => {
    return storedIDs ? !!storedIDs[id]?.closed : false;
  };

  // handleClose is a function to handle the event of banner closure.
  // This function updates the state of storedIDs, marking the banner with
  // the given id as closed.
  const dismissBanner = (id: string) => () => {
    setStoredIDs(prevStoredIDs => ({
      ...prevStoredIDs,
      [id]: { closed: true },
    }));
  };

  const openLegacySiteWideBanner =
    legacySiteWideBanner && !isBannerDismissed(legacySiteWideBanner.id)
      ? legacySiteWideBanner
      : null;
  const openLegacySlimBanner =
    legacySlimBanner && !isBannerDismissed(legacySlimBanner.id)
      ? legacySlimBanner
      : null;
  const openGraphqlBanner =
    (graphQLBanners || []).filter(banner => !isBannerDismissed(banner.id))[0] ||
    null;

  const banner =
    openLegacySiteWideBanner || openLegacySlimBanner || openGraphqlBanner;

  const { ref, EventProvider, reportSelectEvent } = useViewEvent({
    schema: 'view',
    name: 'banner',
    content_id: banner?.id,
  });

  if (!banner) {
    return null;
  }

  const handleClose = () => {
    dismissBanner(banner.id)();
    reportSelectEvent({
      schema: 'select',
      name: 'banner',
      action_name: 'close_banner',
      content_id: banner.id,
    });
  };

  const renderBanner = () => {
    if (openLegacySiteWideBanner) {
      return (
        <LegacySiteWide
          source={openLegacySiteWideBanner}
          onClose={handleClose}
        />
      );
    }

    if (openLegacySlimBanner) {
      return <LegacySlim source={openLegacySlimBanner} onClose={handleClose} />;
    }

    if (openGraphqlBanner) {
      return <GraphqlBanner source={openGraphqlBanner} onClose={handleClose} />;
    }

    return null;
  };

  return (
    <EventProvider>
      <div ref={ref}>{renderBanner()}</div>
    </EventProvider>
  );
};
